import React from 'react';
import CountDown from './count-down';
import {Image} from '@/components-v2';
import './digit-card.css';
import prize from '../../assets/prize.webp';
import {useTranslation} from 'react-i18next';
import {formatDate, goTo, toPriceStr} from '@/utils';
import GameContainer from './game-container';
import {DigitListItem} from '@/pages/home/home.type';
import {CurrencyMap} from '@services/currency';
import globalStore from '@services/global.state';

const LinearGradientList = [
  'linear-gradient(180deg, #8AB105 0%, #528C08 100%)',
  'linear-gradient(180deg, #C10233 0%, #831A8F 100%)',
  'linear-gradient(180deg, #1DC170 0%, #129BB1 100%)',
  'linear-gradient(180deg, #1071C4 0%, #8803C7 100%)',
  'linear-gradient(180deg, #C56226 0%, #242EC2 100%)',
  'linear-gradient(180deg, #C56226 0%, #242EC2 100%)',
  'linear-gradient(180deg, #A47701 0%, #AF9201 100%)',
  'linear-gradient(180deg, #BA1F23 0%, #CB8C23 100%)',
  'linear-gradient(180deg, #790263 0%, #2B1B7D 100%)',
  'linear-gradient(180deg, #93002C 0%, #541D6E 100%)',
  'linear-gradient(180deg, #FF6C72 0%, #E36911 100%)',
  'linear-gradient(180deg, #E823BC 0%, #BB2CFF 100%)',
  'linear-gradient(180deg, #6223E8 0%, #5178FF 100%)',
  'linear-gradient(180deg, #F4AE44 0%, #EE4F0A 100%)',
  'linear-gradient(180deg, #3784F7 0%, #37D6F8 100%)',
  'linear-gradient(180deg, #F14990 0%, #CF14E0 100%)',
  'linear-gradient(180deg, #5162FC 0%, #2CC0FF 100%)',
];

const digitCardBaseStyle = {
  width: 'calc((100% - 0.5rem) / 2)',
  height: '6.3125rem',
  padding: '0.5rem',
  backgroundSize: 'cover',
};

const nextBookingTextStyle = {
  fontSize: '0.375rem',
};

const getTimer = (time: number, cycle: number) => {
  if (!time) {
    return 0;
  }
  const date = formatDate(time, 'yyyy-MM-dd 00:00:00');
  if (date === 'Invalid Date') {
    return 0;
  }
  return (
    cycle * 60 -
    Math.round(((time - new Date(date).getTime()) % (cycle * 60000)) / 1000)
  );
};

const DigitCard = ({
  v,
  serverTime = 0,
  currentTime = 0,
  index,
}: {
  v: DigitListItem;
  index: number;
  serverTime?: number;
  currentTime?: number;
}) => {
  const {t} = useTranslation();
  const toGame = () => {
    globalStore.updateAmount.next({gameType: '3Digit'});
    goTo('Digit', {
      id: v.id,
      pickName: v.pickName,
      pickGameType: v.cycle ? 'quick' : 'normal',
      cycle: v.cycle,
    });
  };

  return (
    <GameContainer
      closed={v.drawTime <= 0 && !v.cycle}
      gameName={v.pickName}
      onClick={toGame}
      className="rounded-lg flex flex-col justify-between overflow-hidden"
      style={{
        ...digitCardBaseStyle,
        backgroundImage: v.pickBackImg
          ? `url(${v.pickBackImg})`
          : LinearGradientList[index],
      }}>
      <div className="flex flex-row items-start justify-between">
        <div className="w-[3.25rem] h-[3.25rem] mr-1">
          <Image
            lazy
            src={v.pickLogo}
            alt="prize"
            className="w-[3.25rem] h-[3.25rem] object-contain"
          />
        </div>
        <div className="flex flex-1 flex-col items-end">
          <div className="flex flex-row items-center">
            <div className="w-[1.3125rem] h-[1.3125rem] mr-1">
              <Image
                src={prize}
                alt="prize"
                className="w-[1.3125rem] h-[1.3125rem] object-contain"
              />
            </div>
            <p className="font-black text-white text-sm">
              {CurrencyMap.default}
              {v.winAmount}
            </p>
          </div>
          <div className="digit-name">
            <p className="text-10 font-black">{v.pickName}</p>
          </div>
        </div>
      </div>

      <div className="flex flex-row items-end justify-between">
        <div className="flex flex-col items-start">
          <p
            style={nextBookingTextStyle}
            className="mb-1 whitespace-nowrap font-black leading-none text-white">
            {t('home:label.nextBooking')}
          </p>
          <CountDown
            reverse
            remain={
              v.cycle
                ? getTimer(v.currentTimestamp, v.cycle)
                : Math.round(v.drawTime / 1000)
            }
            serverTime={serverTime}
            currentTime={currentTime}
            resetTime={v.cycle ? v.cycle * 60 : 0}
          />
        </div>
        <div className="flex flex-col items-end">
          <p className="text-8 font-black leading-none text-white">
            {toPriceStr(v.sellAmount, {showCurrency: false})}/
            {t('home.digit.perTicket')}
          </p>
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              toGame();
            }}
            className="mt-1 flex h-5 w-[3.125rem] items-center justify-center rounded-[0.625rem] border-0 bg-white p-0 text-xs font-black uppercase leading-none text-[#F22D13] shadow-none">
            {t('home.digit.play')}
          </button>
        </div>
      </div>
    </GameContainer>
  );
};

export default DigitCard;
